import { useQuery } from "@apollo/client";
import { useMemo, useState } from "react";
import { ListUserFiles } from "../../../graphql/queries/fileManager";
import { ITEMS_PER_PAGE, OPERATION_TYPE } from "../utils/fileManagerConstants";

/**
 * Custom hook for managing file manager modal data
 * @param {Object} params - Hook parameters
 * @param {string} params.context - File context (LEADERBOARD, CONSUMER_REWARD, CODE_GROUP)
 * @param {string} params.entityId - Entity ID
 * @param {string} params.operationType - Operation type filter (EXPORT, IMPORT, ALL)
 * @param {boolean} params.isOpen - Whether modal is open
 * @param {Object} params.filters - Filter values (status, fromDate, toDate)
 * @returns {Object} Hook return values
 */
export const useFileManagerModal = ({
  isOpen,
  contentType,
  activeTab,
  filters = {},
}) => {
  const [exportPage, setExportPage] = useState(1);
  const [importPage, setImportPage] = useState(1);

  // Determine which page to use based on active tab
  const currentPage = activeTab === "exports" ? exportPage : importPage;
  const currentOperationType =
    activeTab === "exports" ? OPERATION_TYPE.EXPORT : OPERATION_TYPE.IMPORT;

  // Main query for the active tab with pagination
  const buildVariables = () => {
    const variables = {
      contentType,
      operationType: currentOperationType,
      limit: ITEMS_PER_PAGE,
      offset: (currentPage - 1) * ITEMS_PER_PAGE,
    };

    if (filters.status?.[0]?.id) {
      variables.status = filters.status[0]?.id;
    }
    if (filters.context?.[0]?.id) {
      variables.context = filters.context[0]?.id;
    }
    if (filters.fromDate) {
      variables.from = filters.fromDate;
    }
    if (filters.toDate) {
      variables.to = filters.toDate;
    }

    return variables;
  };

  const { data, loading, error, refetch } = useQuery(ListUserFiles, {
    variables: buildVariables(),
    skip: !contentType || !isOpen,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  // Query for export count (for tab badge)
  const { data: exportData } = useQuery(ListUserFiles, {
    variables: {
      contentType,
      operationType: OPERATION_TYPE.EXPORT,
      limit: 0,
      offset: 0,
    },
    skip: !contentType || !isOpen,
    fetchPolicy: "cache-and-network",
  });

  // Query for import count (for tab badge)
  const { data: importData } = useQuery(ListUserFiles, {
    variables: {
      contentType,
      operationType: OPERATION_TYPE.IMPORT,
      limit: 0,
      offset: 0,
    },
    skip: !contentType || !isOpen,
    fetchPolicy: "cache-and-network",
  });

  const files = useMemo(() => {
    const items = data?.listUserFiles?.items || [];
    return items;
  }, [data]);

  const totalCount = data?.listUserFiles?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  // Get counts for tab badges
  const exportCount = exportData?.listUserFiles?.totalCount || 0;
  const importCount = importData?.listUserFiles?.totalCount || 0;

  const handlePageChange = (newPage) => {
    if (activeTab === "exports") {
      setExportPage(newPage);
    } else {
      setImportPage(newPage);
    }
  };

  const resetPagination = () => {
    setExportPage(1);
    setImportPage(1);
  };

  const handleRefetch = async () => {
    try {
      await refetch();
    } catch (err) {
      console.error("Error refetching files:", err);
    }
  };

  return {
    files,
    loading,
    error,
    totalCount,
    totalPages,
    page: currentPage,
    exportPage,
    importPage,
    setPage: handlePageChange,
    resetPagination,
    refetch: handleRefetch,
    exportCount,
    importCount,
  };
};
